import { Box } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { SlidersHorizontal } from 'lucide-react';
import { useCallback, useState } from 'react';
import { PageLayout } from 'src/components/Page';
import { useManagePartnerSettingsValue } from 'src/graphql';
import { useGroupRules } from 'src/graphql/entities/group-rules/group-rules';
import { UserGroupRules } from 'src/graphql/entities/group-rules/group-rules.types';
import { useGlobalStore } from 'src/store';
import { useUrlParamTab } from 'src/utils/hooks';
import { AiPreferences } from './components/ai-preferences';
import { PreferencesForm } from './preferences-form';
import { PreferencesChange, SettingsPreferencesFormFields } from './types';

enum PreferencesTab {
  General = 'general',
  Ai = 'ai',
}

export const Preferences = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { updatePartnerSettingsValue, createPartnerSettingsValue } =
    useManagePartnerSettingsValue(selectedPartnerId);

  const { checkValidity } = useGroupRules();
  const canManageAiSettings = checkValidity([UserGroupRules.AccessToManageAiSettings]);

  const tabItems = [
    { value: PreferencesTab.General, label: 'General' },
    ...(canManageAiSettings ? [{ value: PreferencesTab.Ai, label: 'AI' }] : []),
  ];

  const { activeTab, setActiveTab } = useUrlParamTab<PreferencesTab>({
    paramName: 'tab',
    validValues: tabItems.map((tab) => tab.value),
    defaultValue: PreferencesTab.General,
  });

  const [savingFields, setSavingFields] = useState<Set<SettingsPreferencesFormFields>>(
    () => new Set(),
  );

  const handleChange = useCallback(
    async (data: PreferencesChange) => {
      const stringRuleValue = typeof data.value === 'string' ? data.value : String(data.value);
      const rule = data.rule;
      setSavingFields((prev) => new Set(prev).add(rule));
      try {
        if (data.id && data.partnerId === selectedPartnerId) {
          await updatePartnerSettingsValue({
            partnerSettingValueId: data.id,
            value: stringRuleValue,
            partnerSettingId: rule,
          });
        } else {
          await createPartnerSettingsValue({
            value: stringRuleValue,
            partnerSettingId: rule,
          });
        }
      } finally {
        setSavingFields((prev) => {
          const next = new Set(prev);
          next.delete(rule);
          return next;
        });
      }
    },
    [selectedPartnerId, updatePartnerSettingsValue, createPartnerSettingsValue],
  );

  const generalTab = (
    <PreferencesForm handleChange={handleChange} savingFields={savingFields} />
  );

  return (
    <V2ThemeProvider>
      <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
        <PageLayout
          title="Preferences"
          subtitle="Manage your preferences and toggle features on and off for the platform."
          icon={<SlidersHorizontal />}
          tabs={{ value: activeTab, onChange: setActiveTab, items: tabItems }}
        >
          {activeTab === PreferencesTab.Ai ? <AiPreferences /> : generalTab}
        </PageLayout>
      </Box>
    </V2ThemeProvider>
  );
};
